// IconPickerButton.jsx

import React, { useCallback, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './IconPickerButton.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Popup from 'components/popup/Popup';
import IconPicker from 'components/iconpicker/IconPicker';

/**
 * IconPickerButton
 *
 * Renders an icon button that manages its own IconPicker popup.
 *
 * @param {string} selectedIconKey - Currently selected icon key.
 * @param {function} onSelect - Callback invoked when an icon is selected.
 * @param {function} onClear - Optional callback invoked when the clear action is selected.
 * @param {boolean} disabled - Whether the trigger is disabled.
 * @param {boolean} saving - Whether the trigger should show a saving spinner overlay.
 * @param {number|string} buttonSize - CSS size for the trigger surface.
 * @param {number|string} iconSize - CSS size for the icon.
 * @param {number|string} popupWidth - CSS width for the picker popup.
 * @param {number|string} popupHeight - CSS height for the picker popup.
 * @param {number|string} borderRadius - CSS border radius for the trigger surface.
 * @param {string} backgroundColor - Idle background color for the trigger surface.
 * @param {string} hoverBackgroundColor - Hover background color for the trigger surface.
 * @param {string} iconColor - Icon and spinner accent color.
 * @param {string} title - Title shown for the trigger surface.
 * @param {string} disabledTitle - Title shown while the trigger is disabled.
 * @param {string} className - Optional class name applied to the trigger surface.
 *
 * @returns {ReactNode} The rendered icon picker button.
 */
const IconPickerButton = React.memo(({
    selectedIconKey = '',
    onSelect,
    onClear,
    disabled = false,
    saving = false,
    buttonSize = 46,
    iconSize = 30,
    popupWidth = 400,
    popupHeight = 400,
    borderRadius = 8,
    backgroundColor = 'transparent',
    hoverBackgroundColor,
    iconColor,
    title = 'Choose icon',
    disabledTitle = '',
    className = '',
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [hovered, setHovered] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);

    // ----- REFS ----- //

    const anchorRef = useRef(null);

    // ----- MEMOS ----- //

    const resolvedButtonClassName = useMemo(() => {
        const classNames = ['icon-picker-button-trigger'];
        if (disabled) classNames.push('disabled');
        if (saving) classNames.push('saving');
        if (className) classNames.push(className);
        return classNames.join(' ');
    }, [className, disabled, saving]);

    const resolvedHoverBackgroundColor = hoverBackgroundColor || theme.backgroundHover;
    const resolvedIconColor = iconColor || theme.foreground;
    const isDisabled = disabled || saving;

    // ----- UTILITIES ----- //

    const toCssValue = useCallback((value) => (
        typeof value === 'number' ? `${value}px` : value
    ), []);

    // ----- HANDLERS ----- //

    const closePicker = useCallback(() => {
        setPickerOpen(false);
    }, []);

    const handleToggle = useCallback(() => {
        if (isDisabled) return;
        setPickerOpen((prev) => !prev);
    }, [isDisabled]);

    const handleSelect = useCallback((nextIconKey) => {
        if (typeof onSelect === 'function') {
            onSelect(nextIconKey, { closePicker });
            return;
        }

        closePicker();
    }, [closePicker, onSelect]);

    const handleClear = useCallback(() => {
        if (typeof onClear === 'function') {
            onClear({ closePicker });
            return;
        }

        if (typeof onSelect === 'function') {
            onSelect('', { closePicker });
            return;
        }

        closePicker();
    }, [closePicker, onClear, onSelect]);

    // ----- RENDER ----- //

    return (
        <>

            {/* BUTTON */}
            <button
                className={resolvedButtonClassName}
                style={{
                    backgroundColor: hovered && !isDisabled ? resolvedHoverBackgroundColor : backgroundColor,
                    borderRadius: toCssValue(borderRadius),
                    height: toCssValue(buttonSize),
                    width: toCssValue(buttonSize),
                }}
                ref={anchorRef}
                type="button"
                aria-label={title}
                aria-busy={saving}
                aria-expanded={pickerOpen}
                aria-haspopup="dialog"
                disabled={isDisabled}
                title={isDisabled ? disabledTitle : title}
                onClick={handleToggle}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <Icon
                    iconKey={selectedIconKey}
                    width={iconSize}
                    height={iconSize}
                    color={resolvedIconColor}
                />

                {saving && (
                    <span
                        className="icon-picker-button-spinner"
                        style={{ borderTopColor: resolvedIconColor }}
                    />
                )}
            </button>

            {/* POPUP */}
            <Popup anchorRef={anchorRef} isOpen={pickerOpen} setOpen={setPickerOpen}>
                <IconPicker
                    width={popupWidth}
                    height={popupHeight}
                    selectedIconKey={selectedIconKey}
                    onSelect={handleSelect}
                    onClear={handleClear}
                />
            </Popup>
        </>
    );
});

// Dev Tools
IconPickerButton.displayName = 'IconPickerButton';

export default IconPickerButton;